/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/String2StringArray.ts
 * @create:      2025-07-01 18:01:14.562
 * @modify:      2025-07-11 21:19:12.957
 * @version:     6.0.0
 * @times:       6
 * @lines:       85
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { Strings_Empty } from "../const/Strings";

/**
 * Converts a specially formatted string into an array of strings.
 * The input string should follow the format: "length1_length2_...@stringData",
 * where "length1_length2_..." is the header indicating the length of each substring in "stringData".
 *
 * @param str - The input string to be converted.
 * @returns An array of strings if the input string is in the correct format, otherwise undefined.
 * 
 * @since 4.0.0
 * @version 2025-07-07
 */
export function String2StringArray(str: string) {
    const index = str.indexOf("@");
    if (index !== -1) {
        const header = str.substring(0, index);
        const items = header.split("_").map(p => Number(p));
        if (items.some(p => Number.isNaN(p))) {
            return undefined;
        }

        const list: string[] = [];

        let start = index + 1;
        for (const item of items) {
            list.push(str.substring(start, start + item));
            start += item;
        }

        return list;
    }

    return undefined;
}

/**
 * Converts an array of strings into a specially formatted string.
 * The output string follows the format: "length1_length2_...@stringData",
 * where "length1_length2_..." is the header indicating the length of each string in the input array,
 * and "stringData" is the concatenation of all strings in the input array.
 *
 * @param strArray - An array of strings to be converted. The function accepts a variable number of string arguments.
 * @returns A specially formatted string if the input array is not empty, otherwise a string with an empty header and empty data.
 * 
 * @since 4.0.0
 * @version 2021-12-11
 */
export function StringArray2String(...strArray: string[]) {
    const header = strArray.map(p => p.length).join("_");
    return header + "@" + strArray.join(Strings_Empty);
}
